/**
 * First-party attributie-snapshot.
 *
 * Wat dit doet:
 *   - leest bij eerste landing van de SPA-sessie een gesloten whitelist
 *     van technische attributie-parameters uit de URL en document.referrer;
 *   - normaliseert landing_page tot pathname (geen querystring/hash);
 *   - normaliseert referrer tot origin (geen pad/querystring/hash);
 *   - bewaart de snapshot in sessionStorage zodat hij over SPA-navigatie
 *     beschikbaar blijft;
 *   - wordt pas naar onze backend gestuurd op het moment dat de gebruiker
 *     daadwerkelijk een leadverzoek verstuurt (door `getAttributionSnapshot()`
 *     in de submit-handler aan te roepen).
 *
 * Geen third-party calls, geen cookies, geen PII.
 */

const STORAGE_KEY = 'msp-attribution-v1';

const STRING_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

type ShortKey = typeof STRING_KEYS[number];

export interface AttributionSnapshot {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  /** Pathname only, no querystring/hash. Bv. "/vergelijken". */
  landing_page?: string;
  /** Origin only, no path/query/hash. Bv. "https://www.google.com". */
  referrer?: string;
  /** ISO timestamp van eerste capture. */
  captured_at: string;
  /** Journey-context (AI-match). Los opgeslagen; overschrijft nooit first-touch. */
  journey_source?: JourneySource;
  journey_source_at?: string;
  ai_match_msp_id?: string;
}

export type JourneySource = 'ai_match';

const JOURNEY_KEY = 'msp-journey-v1';
/** Journey-context vervalt na maximaal twee uur. */
export const JOURNEY_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface JourneyContext {
  journey_source: JourneySource;
  journey_source_at: string;
  ai_match_msp_id?: string;
}

const MAX_SHORT = 200;
const MAX_PATH = 2048;

/** Defensieve PII-filter: drop values met @ of lange digit-only patronen. */
function looksLikePii(value: string): boolean {
  if (value.includes('@')) return true;
  if (/^\d{6,}$/.test(value.trim())) return true;
  return false;
}

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value;
}

function safeRefererOrigin(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  try {
    const u = new URL(raw);
    // Negeer same-origin referrer (geen nuttige attributie).
    if (typeof window !== 'undefined' && u.origin === window.location.origin) {
      return undefined;
    }
    return truncate(u.origin, MAX_PATH);
  } catch {
    return undefined;
  }
}

function safeLandingPath(pathname: string): string | undefined {
  if (!pathname) return undefined;
  // pathname is altijd al zonder query/hash, maar truncate for safety.
  return truncate(pathname, MAX_PATH);
}

/**
 * Roep dit éénmaal aan tijdens de eerste pageload. Schrijft alleen wanneer
 * er nog geen snapshot in sessionStorage staat (first-touch).
 */
export function captureAttributionOnLanding(): void {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return; // first-touch
  } catch {
    return;
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const snap: AttributionSnapshot = {
      captured_at: new Date().toISOString(),
    };

    for (const key of STRING_KEYS) {
      const raw = params.get(key);
      if (!raw) continue;
      const value = truncate(raw.trim(), MAX_SHORT);
      if (!value) continue;
      if (looksLikePii(value)) continue;
      snap[key as ShortKey] = value;
    }

    const landing = safeLandingPath(window.location.pathname);
    if (landing) snap.landing_page = landing;

    const ref = safeRefererOrigin(document.referrer);
    if (ref) snap.referrer = ref;

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(snap));
  } catch {
    // Sessionstorage geblokkeerd of URL parse-fout → stilzwijgend negeren.
  }
}

/**
 * Markeer dat de bezoeker vanuit AI-match doorklikt. Wizard: alleen de bron;
 * profiel: bron + gekozen MSP. Een nieuwe klik vervangt de vorige context,
 * zodat een oud MSP-id nooit aan een ander profiel blijft hangen.
 * Slaat nooit vrije invoer, samenvatting, score, motivatie of evidence op.
 */
export function markJourneySource(source: JourneySource, mspId?: string, now: number = Date.now()): void {
  if (typeof window === 'undefined') return;
  try {
    const ctx: JourneyContext = { journey_source: source, journey_source_at: new Date(now).toISOString() };
    if (mspId && UUID_RE.test(mspId)) ctx.ai_match_msp_id = mspId.toLowerCase();
    sessionStorage.setItem(JOURNEY_KEY, JSON.stringify(ctx));
  } catch {
    // ignore
  }
}

/** Wis de journey-context (na een werkelijk nieuwe lead). First-touch blijft staan. */
export function clearJourneySource(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(JOURNEY_KEY);
  } catch {
    // ignore
  }
}

function readJourney(now: number): JourneyContext | undefined {
  try {
    const raw = sessionStorage.getItem(JOURNEY_KEY);
    if (!raw) return undefined;
    const ctx = JSON.parse(raw) as Partial<JourneyContext>;
    const at = typeof ctx.journey_source_at === 'string' ? Date.parse(ctx.journey_source_at) : NaN;
    if (ctx.journey_source !== 'ai_match' || !Number.isFinite(at) || now - at > JOURNEY_MAX_AGE_MS || at > now + 60_000) {
      sessionStorage.removeItem(JOURNEY_KEY); // verlopen of ongeldig → verwijderen
      return undefined;
    }
    return ctx as JourneyContext;
  } catch {
    return undefined;
  }
}

/**
 * Lees de eerder gecapture snapshot, aangevuld met geldige journey-context.
 * `ai_match_msp_id` wordt alleen meegegeven als het gelijk is aan `opts.mspId`
 * (de MSP waarvoor deze aanvraag is). Geeft undefined als er niets is.
 */
export function getAttributionSnapshot(opts: { mspId?: string; now?: number } = {}): AttributionSnapshot | undefined {
  if (typeof window === 'undefined') return undefined;
  const now = opts.now ?? Date.now();
  let base: AttributionSnapshot | undefined;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as AttributionSnapshot;
      if (parsed && typeof parsed === 'object') base = parsed;
    }
  } catch {
    base = undefined;
  }
  const journey = readJourney(now);
  if (!journey) return base;
  const merged: AttributionSnapshot = {
    ...(base ?? { captured_at: journey.journey_source_at }),
    journey_source: journey.journey_source,
    journey_source_at: journey.journey_source_at,
  };
  delete merged.ai_match_msp_id;
  if (journey.ai_match_msp_id && opts.mspId && journey.ai_match_msp_id === opts.mspId.toLowerCase()) {
    merged.ai_match_msp_id = journey.ai_match_msp_id;
  }
  return merged;
}
